import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState, type FormEvent, type ReactNode } from 'react'

import { Button } from '../../../components/Button'
import { useAuth } from '../../AuthContext'
import {
  TRANSPORT_CONFIG_KEY,
  createArea,
  fetchAreas,
  updateArea,
  type Area,
} from '../../api/transportConfig'
import { StatusPill } from '../../components/StatusPill'
import { SectionState } from './SectionState'
import { errorMessage, retryUnlessForbidden } from './errorMessage'
import { INPUT, LABEL, ZONE_NAMES } from './styles'

type Draft = { name: string; zone: string; travel: string }

const EMPTY: Draft = { name: '', zone: '1', travel: '60' }

/**
 * Pickup areas (spec §C.3.3): the dropdown a customer picks from, each with a
 * fare zone and a planned travel allowance. Zone 0 is outside Greater Accra,
 * quoted by phone. Areas are deactivated, never deleted, so past bookings keep
 * their area.
 */
export function AreasSection(): ReactNode {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: [...TRANSPORT_CONFIG_KEY, 'areas'],
    queryFn: () => fetchAreas(token),
    enabled: Boolean(token),
    retry: retryUnlessForbidden,
  })
  const nameInput = useRef<HTMLInputElement>(null)
  const [editing, setEditing] = useState<Area | null>(null)
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [problem, setProblem] = useState<string | null>(null)
  // A failed (de)activation is shown on its own row, not in the form below.
  const [rowProblem, setRowProblem] = useState<{
    id: string
    text: string
  } | null>(null)

  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: TRANSPORT_CONFIG_KEY })

  const save = useMutation({
    mutationFn: (d: { name: string; zone: number; travel_minutes: number }) =>
      editing ? updateArea(token, editing.id, d) : createArea(token, d),
    onSuccess: () => {
      setEditing(null)
      setDraft(EMPTY)
      setProblem(null)
      refresh()
    },
    onError: (e) => setProblem(errorMessage(e)),
  })

  const toggle = useMutation({
    mutationFn: (area: Area) =>
      updateArea(token, area.id, { active: !area.active }),
    onSuccess: () => {
      setRowProblem(null)
      refresh()
    },
    onError: (e, area) => setRowProblem({ id: area.id, text: errorMessage(e) }),
  })

  function startEdit(area: Area): void {
    setEditing(area)
    setDraft({
      name: area.name,
      zone: String(area.zone),
      travel: String(area.travel_minutes),
    })
    setProblem(null)
    // The form is below the list (far below at 390px): take the person there.
    nameInput.current?.focus()
  }

  function submit(e: FormEvent): void {
    e.preventDefault()
    const travel = Number(draft.travel)
    if (draft.name.trim() === '') {
      setProblem('Give the area a name.')
      return
    }
    if (!/^\d+$/.test(draft.travel.trim()) || travel > 240) {
      setProblem('Travel allowance is a whole number of minutes, 0 to 240.')
      return
    }
    save.mutate({
      name: draft.name.trim(),
      zone: Number(draft.zone),
      travel_minutes: travel,
    })
  }

  const state = (
    <SectionState
      isPending={query.isPending}
      error={query.error}
      onRetry={() => void query.refetch()}
      what="pickup areas"
    />
  )
  if (!query.data) return state
  const areas = query.data

  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-ink-3">
        The pickup areas a customer chooses from. The zone sets the fare; the
        travel allowance sets the suggested pickup time.
      </p>

      {areas.length === 0 ? (
        <p className="text-sm text-ink-2">
          No pickup areas yet. Add the first one below.
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-line-1 rounded-[12px] border border-line-1">
          {areas.map((area) => (
            <li
              key={area.id}
              className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 text-sm"
            >
              <span className="min-w-0 flex-1 font-medium text-ink-1">
                {area.name}
              </span>
              <span className="text-ink-2">{ZONE_NAMES[area.zone]}</span>
              <span className="text-ink-3">
                {area.travel_minutes} min travel
              </span>
              <StatusPill
                status={area.active ? 'active' : 'inactive'}
                tone={area.active ? 'done' : 'muted'}
                label={area.active ? 'Active' : 'Inactive'}
              />
              <span className="flex gap-2">
                <Button
                  kind="ghost"
                  onClick={() => startEdit(area)}
                  aria-label={`Edit ${area.name}`}
                >
                  Edit
                </Button>
                <Button
                  kind="secondary"
                  onClick={() => toggle.mutate(area)}
                  loading={toggle.isPending && toggle.variables?.id === area.id}
                  aria-label={`${area.active ? 'Deactivate' : 'Reactivate'} ${area.name}`}
                >
                  {area.active ? 'Deactivate' : 'Reactivate'}
                </Button>
              </span>
              {rowProblem?.id === area.id && (
                <span role="alert" className="w-full text-xs text-bed-bad">
                  {rowProblem.text}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      <form
        onSubmit={submit}
        aria-label={editing ? `Edit ${editing.name}` : 'Add a pickup area'}
        className="flex flex-col gap-3 rounded-[12px] border border-line-1 p-4"
      >
        <h2 className="text-sm font-semibold text-ink-1">
          {editing ? `Edit ${editing.name}` : 'Add a pickup area'}
        </h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className={LABEL}>
            Name
            <input
              ref={nameInput}
              className={INPUT}
              value={draft.name}
              maxLength={80}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="Community 5, Tema"
            />
          </label>
          <label className={LABEL}>
            Fare zone
            <select
              className={INPUT}
              value={draft.zone}
              onChange={(e) => setDraft({ ...draft, zone: e.target.value })}
            >
              {[1, 2, 3, 0].map((z) => (
                <option key={z} value={z}>
                  {ZONE_NAMES[z]}
                </option>
              ))}
            </select>
          </label>
          <label className={LABEL}>
            Travel allowance (minutes)
            <input
              className={INPUT}
              inputMode="numeric"
              value={draft.travel}
              onChange={(e) => setDraft({ ...draft, travel: e.target.value })}
            />
          </label>
        </div>
        {problem && (
          <p role="alert" className="text-sm text-bed-bad">
            {problem}
          </p>
        )}
        <span className="flex flex-wrap gap-2">
          <Button type="submit" loading={save.isPending}>
            {editing ? 'Save changes' : 'Add area'}
          </Button>
          {editing && (
            <Button
              kind="ghost"
              onClick={() => {
                setEditing(null)
                setDraft(EMPTY)
                setProblem(null)
              }}
            >
              Cancel
            </Button>
          )}
        </span>
      </form>
    </div>
  )
}
